/* eslint-disable no-nested-ternary */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  boxShadow, maxWidth, min, primaryHeadingColor, retina, secondaryFont, wMBold,
} from 'Utils/utilsStyle';
import { actions as SRIActions } from 'Slice/SRISlice';
import { SRITabFilters } from 'Types/RequestBodies';
import {
  LeaderboardSummary, SRIFilters, SRITeamReadinessTeam, TeamLeaderBoardDataIn,
} from 'Types/SRITypes';
import {
  selectLeaderBoardSelectedRegion,
  selectLeaderBoardSelectedTeam,
  selectLeaderBoardSummaryGraph, selectLeaderBoardSummaryLoading,
  selectOverviewFilter, selectOverviewFilterType,
  selectTeamLeaderBoardGraph, selectTeamLeaderBoardLoading,
} from 'Selector/SRISelector';
import EmptyState from 'Components/common/components/EmptyState';
import { formatDisplayDate } from 'Utils/dateUtils';
import HeadingWithToolTip from 'Components/common/components/HeadingWithToolTip';
import { TOOLTIP_LEADERBOARD } from 'Utils/Constants';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { getCurrentTime, valueToObject } from 'Utils/UtilityFunctions';
import ChartSpinner from './ChartSpinner';

import BeltChart from './BeltChart';
import TeamChartWrapper from './TeamChartWrapper';

const Wrapper = styled.div`
  .title {
    font-size: 25px;
    font-weight: ${wMBold};
    letter-spacing: -0.7px;
    font-family: ${secondaryFont};
    color: ${primaryHeadingColor};
  }
  .date{
    font-size: 16px;
    font-weight: 500;
    letter-spacing: -0.7px;
    font-family: ${secondaryFont};
    color: #7E7E7E;
    display: flex;
    span{
      margin-right: 5px;
      color: #424B50;
    }
  }
  .tabs-item {
    cursor: pointer;
  }
`;

const Overlay = styled.div`
  box-shadow: ${boxShadow};
  border-radius: 10px;
  background: #fff;
  padding: 24px 42px;
  margin-bottom: 24px;
  z-index: 2;
  @media all and (max-width: ${min}){
    padding: 14px 5px;
  }
  .section-detail-tip{
    margin-top: 3px;
    @media all and (max-width: ${maxWidth}) , ${retina} {
    margin-top: 2px;
    }
  }
`;

const LeaderBoardTab = function LeaderBoardTab() {
  const dispatch = useDispatch();
  const SRIFilter: SRIFilters = useSelector(selectOverviewFilter);
  const filtersType: string = useSelector(selectOverviewFilterType);
  const leaderBoardSummaryLoading: boolean = useSelector(selectLeaderBoardSummaryLoading);
  const teamLeaderBoardLoading: boolean = useSelector(selectTeamLeaderBoardLoading);
  const leaderBoardGraph: LeaderboardSummary[] = useSelector(selectLeaderBoardSummaryGraph);
  const selectedTeam: SRITeamReadinessTeam = useSelector(selectLeaderBoardSelectedTeam);
  const selectedRegion = useSelector(selectLeaderBoardSelectedRegion);

  const teamLeaderBoardGraph: TeamLeaderBoardDataIn[] = useSelector(selectTeamLeaderBoardGraph);
  const filteredData = teamLeaderBoardGraph || [];

  function getFiltersObject() {
    const filters = [];
    filters.push({
      type: 'region_filter',
      name: selectedRegion,
    });
    filters.push({ type: 'team_filter', name: selectedTeam.team_name, id: selectedTeam.team_id });
    filters.push({ type: 'team_details', ...SRIFilter });
    return filters;
  }

  useEffect(() => {
    const startTime = getCurrentTime();
    dispatch(flurryActions.callFlurryEvent(valueToObject('sri_leaderboard_loaded', startTime)));
  }, []);

  useEffect(() => {
    if (SRIFilter.end_date !== '' && SRIFilter.start_date !== ''
      && selectedRegion !== '' && Object.keys(selectedTeam).length > 0) {
      const obj: SRITabFilters = {
        filters: getFiltersObject(),
      };
      dispatch(SRIActions.getTeamLeaderBoardGraph(obj));
      dispatch(SRIActions.getLeaderBoardSummaryGraph(obj));
    }
  }, [SRIFilter, selectedRegion, selectedTeam]);

  return (
    <Wrapper>
      <Overlay className="board-container">
        <div className="head-wrap d-flex justify-content-between mb-c-50">
          <h6 className="title mb-0 d-flex align-items-center">
            Leaderboard Summary
            <HeadingWithToolTip
              toolTipText={TOOLTIP_LEADERBOARD}
            />
          </h6>
          {/* <DropdownFilter menu={menu} /> */}
        </div>
        {leaderBoardSummaryLoading ? <ChartSpinner />
          : (
            <BeltChart
              data={leaderBoardGraph}
            />
          )}
      </Overlay>
      <Overlay>
        <div className="head-wrap d-flex flex-column mb-c-20">
          <h6 className="title mb-0">Team Leaderboard</h6>
          <p className="date">
            {SRIFilter.start_date && SRIFilter.end_date && filtersType ? (
              <>
                <span>From:</span>
                {formatDisplayDate(SRIFilter.start_date, 'MM/DD/YYYY')}
                <span />
                |
                <span />
                <span>To:</span>
                {formatDisplayDate(SRIFilter.end_date, 'MM/DD/YYYY')}
              </>
            ) : null}
          </p>
        </div>
        {teamLeaderBoardLoading ? <ChartSpinner />
          : filteredData.length === 0 ? (
            <EmptyState
              msg="No analytics data found"
              detail="Try adjusting your filters to find what you are looking for"
              height="350px"
            />
          ) : <TeamChartWrapper />}
      </Overlay>
    </Wrapper>
  );
};

export default LeaderBoardTab;
